import { NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConnectionStatus } from '@capacitor/network';
import { ActivityLocation, HelperFunctions, PoiRating, RatingType, User } from '@curbnturf/entities';
import { RatingActionTypes, RatingFacade } from '@curbnturf/rating';
import { ReviewPhotosComponent } from '@curbnturf/site-components/src/lib/review-photos/review-photos.component';
import { StatusFacade } from '@curbnturf/status';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { RatingRateComponent } from '../rating-rate/rating-rate.component';

@Component({
  selector: 'curbnturf-rating-poi',
  templateUrl: './rating-poi.component.html',
  styleUrls: ['./rating-poi.component.scss'],
  standalone: true,
  imports: [NgIf, IonicModule, FormsModule, ReactiveFormsModule, RatingRateComponent, ReviewPhotosComponent],
})
export class RatingPoiComponent implements OnInit, OnDestroy {
  @Input() poi: ActivityLocation;

  @Input() appContext: boolean = false;

  subscriptions: Subscription[] = [];
  networkStatus: ConnectionStatus | undefined;

  user: User;

  ratingForm: FormGroup<{
    rating: FormControl<number | null>;
    additionalComments: FormControl<string | null>;
    photos: FormArray<FormControl<string | null>>;
  }>;

  get isOnline(): boolean {
    if (this.networkStatus) {
      return !!this.networkStatus?.connected;
    } else {
      return true;
    }
  }

  getAvatarPhoto = HelperFunctions.getPhotoUrlSquareTiny;

  constructor(
    private updates$: Actions,
    private fb: FormBuilder,
    private loadingCtrl: LoadingController,
    private modalController: ModalController,
    private ratingFacade: RatingFacade,
    private statusFacade: StatusFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.ratingForm = this.fb.group<{
      rating: FormControl<number | null>;
      additionalComments: FormControl<string | null>;
      photos: FormArray<FormControl<string | null>>;
    }>({
      rating: this.fb.control(0, [Validators.required, Validators.min(1), Validators.max(5)]),
      additionalComments: this.fb.control(''),
      photos: this.fb.array<string>([], { updateOn: 'blur' }), // array of URLs
    });

    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.networkStatus = status;
      }),
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onSubmitReview() {
    this.ratingForm.get('rating')?.markAsTouched();
    this.ratingForm.get('additionalComments')?.markAsTouched();

    if (this.ratingForm.dirty && this.ratingForm.valid) {
      const saveableRating = new PoiRating({
        topic: RatingType.overall,
        score: {
          score: this.ratingForm.value.rating || 0,
          description: '',
        },
        additionalComments: this.ratingForm.value.additionalComments || '',
        creator: this.user,
        creatorId: this.user?.id || 0,
        poiRated: { id: this.poi.id },
        poiRatedId: this.poi.id,
        created: DateTime.now().toMillis(),
      });

      const load = await this.loadingCtrl.create({
        message: 'Saving...',
        duration: 5000,
      });
      await load.present();

      // Make the subscription before sending the request to make sure we don't miss anything.
      this.subscriptions.push(
        this.updates$.pipe(ofType(RatingActionTypes.PoiRatingsCreated)).subscribe(async () => {
          await load.dismiss();
          await this.modalController.dismiss();
        }),
      );

      this.ratingFacade.createPoi([saveableRating]);
    } else if (!this.ratingForm.dirty && this.ratingForm.valid) {
      this.onCancelReview();
    }
  }

  uploaded() {
    this.ratingForm.markAsDirty();
  }

  async onCancelReview() {
    await this.modalController.dismiss();
  }
}
